import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link, Typography } from '@mui/material';
import apis from '@src/apis';
import RankTable from '@src/components/RankReport/RankTable';
import Tooltip from '@src/components/Tooltip';
import { formatNumber as formatNumberUtil } from '@src/utils/formatNumber';
import useFormatNumber from '@src/hooks/useFormatNumber';
import { COLOR } from '@src/styles/color';
import LoadingOverview from './LoadingOverview';

const PackageRevenueStats = ({ startTime, endTime, isReLoad }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { formatNumber } = useFormatNumber();

  const organizationId = useSelector(
    (state) => state.user.organization.selected.id,
  );

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isDecrease, setIsDecrease] = useState(true);
  const [total, setTotal] = useState(0);
  const [percent, setPercent] = useState(0);

  const LIMIT = 10;
  const TYPE = t('hasRevenue');
  const OBJECTS = t('pkg');
  const status = isDecrease ? t('highest') : t('lowest');
  const statusTooltip = !isDecrease ? t('highest') : t('lowest');

  const heads = [
    { label: t('packageCode'), valueName: 'packageCode', align: 'left' },
    { label: t('appName'), valueName: 'appInfo', align: 'left' },
    { label: t('Revenue'), valueName: 'revenue', align: 'right' },
    { label: t('changed'), valueName: 'changed', align: 'right' },
  ];

  const colorChanged = (changed) => {
    if (changed > 0) return COLOR.success.base;
    if (changed < 0) return COLOR.error.base;
    return COLOR.blueGray[64];
  };

  const formatData = data.map((item) => ({
    ...item,
    appInfo: (
      <Link
        href={`/organization/${organizationId}/report?appId=${item?.app?.id}&startTime=${startTime}&endTime=${endTime}`}
      >
        {item?.app?.name}
      </Link>
    ),
    revenue: (
      <Tooltip
        title={formatNumberUtil(item?.revenue?.currentRevenue)}
        placement="right"
        arrow
      >
        <Typography>{formatNumber(item?.revenue?.currentRevenue)}</Typography>
      </Tooltip>
    ),
    changed: (
      <Typography
        variant="body2"
        color={() => colorChanged(item?.revenue?.changed)}
      >{`${item?.revenue?.changed > 0 ? '+' : ''}${formatNumber(
        item?.revenue?.changed,
      )}%`}</Typography>
    ),
  }));

  const handleChangeSort = () => {
    setIsDecrease(!isDecrease);
  };

  const fetchAppRevenueStatsData = async () => {
    try {
      setLoading(true);
      const res = await apis.stat.packageRevenueStats(organizationId, {
        startTime,
        endTime,
        limit: LIMIT,
        sortRevenue: isDecrease ? 'desc' : 'asc',
      });
      if (res?.status) {
        const { limitRevenue, data: revenueData, totalRevenue } = res?.result;
        const fixedLimitRevenue = limitRevenue
          ? parseFloat(((limitRevenue / totalRevenue) * 100).toFixed(2))
          : 0;
        setTotal(limitRevenue || 0);
        setPercent(fixedLimitRevenue);
        setData(revenueData || []);
      }
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    } finally {
      setLoading(false);
      if (isFirstLoading) setIsFirstLoading(false);
    }
  };

  useEffect(() => {
    fetchAppRevenueStatsData();
  }, [startTime, endTime, isDecrease, isReLoad]);

  if (loading && isFirstLoading) return <LoadingOverview />;

  return (
    <RankTable
      data={formatData}
      heads={heads}
      title={t('topRank', {
        limit: LIMIT,
        type: TYPE,
        object: OBJECTS,
        status,
      })}
      limit={LIMIT}
      type={TYPE}
      object={OBJECTS}
      status={statusTooltip}
      onChangeSort={handleChangeSort}
      isDecrease={isDecrease}
      loading={loading}
      total={total}
      percent={percent}
    />
  );
};
export default PackageRevenueStats;
